import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "fencing-web-design-colorado",
  metaTitle: "Fencing Contractor Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom fencing web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Fencing Contractor Web Design Colorado",
  eyebrow: "Fencing Contractor Web Design — Colorado",
  h1: (
    <>
      Fencing Contractor <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded fencing websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Fencing websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first fencing sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Fencing Contractor web design in Colorado",
    body: "Fencing Contractor web design is the practice of building conversion-focused, mobile-first websites for fencing businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with portfolio galleries that don't tank PageSpeed." },
    { title: "Material/style service pages", body: "Wood, vinyl, chain-link, aluminum, wrought-iron, privacy, ranch — each ranks independently." },
    { title: "Portfolio galleries", body: "Lazy-loaded project galleries with image schema." },
    { title: "Quote forms with linear-foot fields", body: "Qualifying intake with linear footage and material preference to filter real buyers." },
    { title: "HOA + permit guidance content", body: "CO-specific HOA and permit info that captures long-tail traffic." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Material-type pages outrank generic fence pages", body: "Vinyl, wood, and chain-link have different prices and different buyers — separate pages each." },
    { heading: "Portfolio sells the bid", body: "Fencing is a portfolio sale — galleries above the fold close the estimate." },
    { heading: "HOA guidance content earns trust", body: "CO HOA fence rules drive long-tail traffic that converts into qualified leads." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a fencing website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a fencing website?", a: "Typical timeline is 4–6 weeks for a 12–20 page fencing site with full service and service-area pages. Larger multi-location fencing businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs fencing businesses?", a: "No — we build fencing sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Fencing Contractor Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for fencing businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/fencing-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
